import React, { DependencyList, useCallback, useEffect, useState } from 'react'
import { Updater, useImmer } from 'use-immer'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'

export type ApiData<T> = {
    data: T | null,
    loading: boolean,
    error: unknown,
    setData: Updater<T | null>,
    reload: () => void,
}

// Uniform data-fetch state, replacing the hand-rolled `data === null ? 'Laddar…'`
// pattern. On failure `loading` clears and `error` is set, so a panel can show a
// retry affordance instead of spinning forever; the error is also reported to the
// global bus by parseResponse, so the snackbar fires too. `setData` is an immer
// updater for panels that edit the fetched data locally; `reload` re-runs the fetch.
//
// The fetch runs directly (not via the call chain): these are reads, the global
// lock only applies to mutations, and parseResponse already routes errors to the bus.
export function useApiData<T>(
    fetcher: () => Promise<T>,
    deps: DependencyList,
    options: { enabled?: boolean } = {},
): ApiData<T> {
    const enabled = options.enabled ?? true
    const [data, setData] = useImmer<T | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<unknown>(null)
    const [nonce, setNonce] = useState(0)

    useEffect(() => {
        if (!enabled) return
        let cancelled = false
        setLoading(true)
        setError(null)
        fetcher()
            .then(result => { if (!cancelled) { setData(result); setLoading(false) } })
            .catch((e: unknown) => { if (!cancelled) { setError(e); setLoading(false) } })
        return () => { cancelled = true }
        // fetcher is intentionally not a dep; callers pass the data deps explicitly.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, enabled, nonce])

    const reload = useCallback(() => { setError(null); setNonce(n => n + 1) }, [])
    return { data, loading, error, setData, reload }
}

// The shared initial-load indicator: one spinner everywhere a panel is waiting for
// its first data, instead of ad-hoc 'Laddar…' text.
export function Loading() {
    return (
        <Box sx={{ p: 2 }}>
            <CircularProgress />
        </Box>
    )
}

// The panel-level load-failure affordance (not an inline/field message): the global
// snackbar already says what went wrong; this just replaces the dead spinner.
export function LoadError({ onRetry }: { onRetry: () => void }) {
    return (
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>Kunde inte ladda.</span>
            <Button size="small" variant="outlined" onClick={onRetry}>Försök igen</Button>
        </Box>
    )
}
